import { rgba, semantic } from '@repo/design-tokens';

import { REGION_STYLE } from '../../components/PolygonPlotter2/regionStyle';
import type { ReadoutTone } from './calibrationModel';
import type { CalibrationStatusState } from './calibrationStatusText';

/**
 * Every colour the calibration screen and the camera panel's calibration row use to show a status
 * (`observer-matching-beyond-geometry-calibration-palette`): ONE table, each colour a status role
 * from Rob's status page (`semantic.status`), so a hue means the same here as on every other
 * onboarding step. No colour palette document covers calibration yet; Mike ruled on 2026-10-30
 * that it takes the roles, and Rob reviews the mapping after it ships (card 26's `PROVENANCE.md`).
 *
 * - The readout and tile labels: no spots neutral, counting towards six work to do, fitted all
 *   done, any fit trouble (can't fit, too close to a line, poor fit, bad spots) a problem with a
 *   fix. The words say which trouble; the colour never stands alone.
 * - The picture overlay: rooms and machines are drawn exactly as the Observers step draws its
 *   context and its machine outlines (`REGION_STYLE.ghost`, `REGION_STYLE.machineOutline`), a
 *   spot's error line is a problem, and the corner being asked for (its machine on the plan, the
 *   picture crosshair) is being edited. The camera on the plan pane is neutral: it marks where the
 *   picture is taken from, not a status.
 * - The camera panel row: calibrated all done, out of date work to do, the rest neutral, since
 *   nothing requires a calibration.
 *
 * `semantic.text.primary`, the row's ink where it shows no status, is the one non-status colour;
 * `calibrationLook.test.ts` holds the rest to the roles.
 */
const { roles } = semantic.status;

const tone = (role: keyof typeof roles) => ({ dot: roles[role].mark, ink: roles[role].ink });

export const CALIBRATION_LOOK = {
  readout: {
    idle: tone('neutral'),
    exact: tone('workToDo'),
    good: tone('allDone'),
    warn: tone('problem'),
    bad: tone('problem'),
  } satisfies Record<ReadoutTone, { dot: string; ink: string }>,
  overlay: {
    room: {
      stroke: REGION_STYLE.ghost.stroke,
      width: REGION_STYLE.ghost.width,
      dash: REGION_STYLE.ghost.dash,
    },
    machine: {
      stroke: REGION_STYLE.machineOutline.stroke,
      width: REGION_STYLE.machineOutline.width,
      dash: REGION_STYLE.machineOutline.dash,
      lineCap: REGION_STYLE.machineOutline.lineCap,
    },
    spotError: { stroke: roles.problem.mark, width: 2.5 },
    targetMachine: {
      stroke: roles.beingEdited.mark,
      fill: rgba(roles.beingEdited.mark, 0.22),
    },
    crosshair: roles.beingEdited.mark,
    // The camera on the plan pane (findable-spots R1.2): where the picture is taken from, not a status.
    camera: { stroke: roles.neutral.mark, fill: rgba(roles.neutral.mark, 0.12) },
  },
  row: {
    'no-stream': { dot: roles.neutral.mark, ink: semantic.text.primary },
    'no-picture': { dot: roles.neutral.mark, ink: semantic.text.primary },
    'not-calibrated': { dot: roles.neutral.mark, ink: semantic.text.primary },
    calibrated: tone('allDone'),
    'out-of-date': { dot: roles.workToDo.mark, ink: semantic.text.primary },
  } satisfies Record<CalibrationStatusState, { dot: string; ink: string }>,
} as const;
